import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";
import { cn } from "@u/cn";
import { ArrowUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DashboardSortOption } from "./useCollection";
import type { DashboardSort } from "./queryState";

export interface DashboardSortSelectProps {
  options: DashboardSortOption[];
  value: DashboardSort | null;
  onChange(sort: DashboardSort | null): void;
  className?: string;
}

const tokenOf = (s: { id: string; dir: DashboardSort["dir"] }) =>
  `${s.id}.${s.dir}`;

/**
 * Sort control for non-tabular collections (List/Grid have no clickable
 * column headers). Each option is an explicit (field, direction) pair, e.g.
 * newest = `{ id: "created_at", dir: "desc", label: "Newest" }`. Writes to the
 * shared query-state via `onChange`.
 */
export function DashboardSortSelect({
  options,
  value,
  onChange,
  className,
}: DashboardSortSelectProps) {
  const { t } = useTranslation();
  const current = value ? tokenOf(value) : "";

  return (
    <Select
      value={current}
      onValueChange={(v) => {
        const opt = options.find((o) => tokenOf(o) === v);
        onChange(opt ? { id: opt.id, dir: opt.dir } : null);
      }}
    >
      <SelectTrigger
        size="sm"
        aria-label={t("sort")}
        icon={ArrowUpDown}
        className={cn("w-auto", className)}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent position="popper">
        {options.map((opt) => (
          <SelectItem key={tokenOf(opt)} value={tokenOf(opt)}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
